import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import couponReducer from "./slices/coupon.slice";
import discountReducer from "./slices/discount.slice";
import createSagaMiddleware from "redux-saga";
import rootSaga from "./sagas";
import productsReducer from "@/store/slices/productNew.slice";
import categoryReducer from "@/store/slices/category.slice";
import collectionsReducer from "@/store/slices/collection.slice";
import InventoryReducer from "@/store/slices/Inventory.slice";
import authReducer from "@/store/slices/auth.slice";
import kycReducer from "@/store/slices/kyc.slice";
import pageReducer from "@/store/slices/page.slice";
import assetsReducer from "@/store/slices/assets.slice";
import uiReducer from "@/store/slices/ui.slice";
import webbuilderReducer from "@/store/slices/webbuilderSlice";
import orderReducer from "@/store/slices/order.slice";
import taxReducer from "@/store/slices/tax.slice";
import sseReducer from "@/store/slices/sse.slice";
import ticketsReducer from "@/store/slices/ticket.slice";
import customersReducer from "@/store/slices/customer.slice";
import chargeReducer from "@/store/slices/charge.slice";
import marketingCampaignReducer from "@/store/slices/marketingCampaign.slice";
import marketingAutomationReducer from "@/store/slices/marketingAutomation.slice";
import abandonedCartReducer from "@/store/slices/abandonedCart.slice";
import marketingTemplateReducer from "@/store/slices/marketingTemplate.slice";
import metafieldReducer from "@/store/slices/metafield.slice";
import dashboardReducer from "@/store/slices/dashboard.slice";
import walletReducer from "@/store/slices/wallet.slice";
import settingsReducer from "@/store/slices/settings.slice";
import domainReducer from "@/store/slices/domain.slice";
import subscriptionReducer from "@/store/slices/subscription.slice";
import notificationsReducer from "@/store/slices/notifications.slice";
import permissionReducer from "@/store/slices/permission.slice";
import pickupLocationReducer from "@/store/slices/pickupLocation.slice";
import tenantReducer from "@/store/slices/tenant.slice";
import multiTenantMiddleware from "@/store/middleware/multiTenant.middleware";

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "coupon",
    "discount",
    "products",
    "category",
    "collections",
    "kyc",
    "page",
    "assets",
    "webbuilder",
    "order",
    "tax",
    "tickets",
    "customers",
    "charges",
    "marketingCampaigns",
    "marketingAutomations",
    "abandonedCart",
    "marketingTemplates",
    "metafields",
    "dashboard",
    "wallet",
    "settings",
    "domains",
    "subscription",
    "notifications",
    "permissions",
    "pickupLocations",
    "tenant", // Multi-tenant context (DO NOT clear on logout)
  ], // Persist all reducers
};

const rootReducer = combineReducers({
  coupon: couponReducer,
  discount: discountReducer,
  products: productsReducer,
  category: categoryReducer,
  collections: collectionsReducer,
  Inventory: InventoryReducer,
  auth: authReducer,
  kyc: kycReducer,
  page: pageReducer,
  assets: assetsReducer,
  ui: uiReducer,
  webbuilder: webbuilderReducer,
  order: orderReducer,
  tax: taxReducer,
  sse: sseReducer,
  tickets: ticketsReducer,
  customers: customersReducer,
  charges: chargeReducer,
  marketingCampaigns: marketingCampaignReducer,
  marketingAutomations: marketingAutomationReducer,
  abandonedCart: abandonedCartReducer,
  marketingTemplates: marketingTemplateReducer,
  metafields: metafieldReducer,
  dashboard: dashboardReducer,
  wallet: walletReducer,
  settings: settingsReducer,
  domains: domainReducer,
  subscription: subscriptionReducer,
  notifications: notificationsReducer,
  permissions: permissionReducer,
  pickupLocations: pickupLocationReducer,
  tenant: tenantReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);
const sagaMiddleware = createSagaMiddleware();

// Debug middleware to log webbuilder actions
const webbuilderLogger = (store: any) => (next: any) => (action: any) => {
  if (action.type && action.type.startsWith('webbuilder/')) {
    console.log('🟣 Middleware: webbuilder action dispatched:', {
      type: action.type,
      payload: action.payload,
    });
  }
  const result = next(action);
  if (action.type && action.type.startsWith('webbuilder/')) {
    console.log('🟣 Middleware: State after action:', store.getState().webbuilder);
  }
  return result;
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          // Ignore KYC actions that contain FormData
          "kyc/submitBusinessDetailsRequest",
          "kyc/submitPersonalDetailsRequest",
          "kyc/uploadDocumentsRequest",
          "kyc/uploadAddressProofRequest",
          "kyc/uploadVideoKYCRequest",
        ],
        ignoredActionsPaths: ["payload.formData"],
      },
    }).concat(multiTenantMiddleware, sagaMiddleware, webbuilderLogger),
});

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
